// src/pages/Assets.jsx  (ADMIN)

import { useEffect, useMemo, useState } from "react";
import {
  Box, Card, CardContent, Typography, Stack, TextField, InputAdornment,
  List, ListItemButton, ListItemText, Divider, Chip, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel,
  Tooltip, Avatar, Table, TableHead, TableRow, TableCell, TableBody,
  LinearProgress, Alert, Paper
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UndoIcon from "@mui/icons-material/Undo";
import ImageIcon from "@mui/icons-material/Image";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import PowerIcon from "@mui/icons-material/Power";
import SdStorageIcon from "@mui/icons-material/SdStorage";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import MouseIcon from "@mui/icons-material/Mouse";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import VideocamIcon from "@mui/icons-material/Videocam";
import SpeakerIcon from "@mui/icons-material/Speaker";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import TabletMacIcon from "@mui/icons-material/TabletMac";
import SimCardIcon from "@mui/icons-material/SimCard";
import CableIcon from "@mui/icons-material/Cable";
import UsbIcon from "@mui/icons-material/Usb";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ChairIcon from "@mui/icons-material/Chair";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import InventoryIcon from "@mui/icons-material/Inventory";
import AppleIcon from "@mui/icons-material/Apple";
import WindowsIcon from "@mui/icons-material/Window";

import { db, storage } from "../../firebase";
import { onValue, ref as rtdbRef, push, update, set, remove } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

/* ------------------------ Categories (searchable + icons) ------------------------ */

const CATEGORY_KEYS = [
  "Laptop",
  "Laptop Charger / Adapter",
  "Desktop PC / Workstation",
  "Monitor",
  "Keyboard",
  "Mouse",
  "Headphones / Headset",
  "Webcam",
  "Speakers",
  "External HDD / SSD",
  "USB / Pendrive",
  "Cables",
  "Backpack / Laptop Bag",
  "Mobile Phone",
  "Tablet / iPad",
  "SIM Card",
  "Stationery / Diary",
  "Chair",
  "Desk / Table",
  "Other",
];

const iconFor = (cat = "") => {
  const k = String(cat).toLowerCase();
  if (k.includes("laptop")) return <LaptopMacIcon />;
  if (k.includes("desktop") || k.includes("workstation") || k.includes("monitor") || k.includes("display") || k.includes("screen")) return <DesktopWindowsIcon />;
  if (k.includes("keyboard")) return <KeyboardIcon />;
  if (k.includes("mouse")) return <MouseIcon />;
  if (k.includes("headphone") || k.includes("headset") || k.includes("earphone")) return <HeadphonesIcon />;
  if (k.includes("webcam")) return <VideocamIcon />;
  if (k.includes("speaker")) return <SpeakerIcon />;
  if (k.includes("hdd") || k.includes("ssd") || k.includes("hard disk") || k.includes("drive")) return <SdStorageIcon />;
  if (k.includes("usb") || k.includes("pendrive") || k.includes("pen drive")) return <UsbIcon />;
  if (k.includes("charger") || k.includes("adapter") || k.includes("power")) return <PowerIcon />;
  if (k.includes("cable") || k.includes("type-c") || k.includes("hdmi") || k.includes("thunderbolt")) return <CableIcon />;
  if (k.includes("bag") || k.includes("backpack")) return <WorkOutlineIcon />;
  if (k.includes("mobile") || k.includes("phone") || k.includes("smartphone")) return <SmartphoneIcon />;
  if (k.includes("tablet") || k.includes("ipad")) return <TabletMacIcon />;
  if (k.includes("sim")) return <SimCardIcon />;
  if (k.includes("stationery") || k.includes("diary")) return <Inventory2Icon />;
  if (k.includes("chair")) return <ChairIcon />;
  if (k.includes("desk") || k.includes("table")) return <TableRestaurantIcon />;
  return <DevicesOtherIcon />;
};

const matchCategories = (q) => {
  const t = (q || "").trim().toLowerCase();
  if (!t) return CATEGORY_KEYS;
  const hits = CATEGORY_KEYS.filter(c => c.toLowerCase().includes(t));
  return hits.length ? hits : ["Other"];
};

/* ------------------------ Misc helpers ------------------------ */

const fmtDate = (v) => {
  if (!v) return "";
  const d = typeof v === "number" ? new Date(v) : new Date(String(v));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const statusChipColor = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "ASSIGNED") return "success";
  if (s === "REPAIR") return "warning";
  if (s === "LOST") return "error";
  if (s === "RETURNED") return "default";
  return "default";
};

// Heuristic for Mac vs Windows laptop buckets
const isLaptop = (row) => (row?.category || "").toLowerCase().includes("laptop");
const isMac = (row) => {
  const hay = `${row?.name || ""} ${row?.notes || ""}`.toLowerCase();
  return hay.includes("mac") || hay.includes("macbook") || hay.includes("apple");
};

/* ============================== Small stat card ============================== */
function StatCard({ icon, label, value, sub }) {
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", minWidth: 180 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: "grey.100" }}>{icon}</Box>
        <Box>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Typography variant="h6" sx={{ lineHeight: 1.1 }}>{value}</Typography>
          {sub ? <Typography variant="caption" color="text.secondary">{sub}</Typography> : null}
        </Box>
      </Stack>
    </Paper>
  );
}

/* ===================================================================================== */

export default function AssetsAdmin() {
  // Employees
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [selectedUid, setSelectedUid] = useState(null);

  // Per-employee assets (right pane)
  const [assets, setAssets] = useState(null);
  const [loadingAssets, setLoadingAssets] = useState(false);

  // Global assets map for stats + global search
  const [allAssetsByUser, setAllAssetsByUser] = useState({});   // {uid: {assetId: row}}
  const [countsByUser, setCountsByUser] = useState({});         // {uid: count}
  const [stats, setStats] = useState({ totalUnits: 0, assigned: 0, laptopsMac: 0, laptopsWin: 0, mouse: 0 });

  // Global search (across all users)
  const [globalQuery, setGlobalQuery] = useState("");
  const [globalResults, setGlobalResults] = useState([]); // [{uid, employeeName, asset}]

  // Dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState({ open: false });

  // Category search in dialog
  const [catSearch, setCatSearch] = useState("");
  const catOptions = useMemo(() => matchCategories(catSearch), [catSearch]);

  // Form
  const [form, setForm] = useState({
    category: "Laptop",
    name: "Laptop",
    serial: "",
    returnable: true,
    status: "ASSIGNED",
    accessories: "",
    assignedAt: "",
    dueReturnAt: "",
    notes: "",
    photoFile: null,
    photoUrl: "",
  });

  /* ---------------- Load employees (active only) ---------------- */
  useEffect(() => {
    const unsub = onValue(rtdbRef(db, "/employees"), (snap) => {
      const raw = snap.val() || {};
      const list = [];
      Object.entries(raw).forEach(([uid, v]) => {
        if (v.onboardingStatus === "OFFBOARDED" || v.active === false) return;
        const name =
          v.fullName ||
          v.submittedProfile?.profileFields?.fullName ||
          v.email?.split?.("@")?.[0] ||
          uid;
        list.push({ uid, name, email: v.email || "" });
      });
      list.sort((a, b) => a.name.localeCompare(b.name));
      setEmployees(list);
      if (!selectedUid && list.length) setSelectedUid(list[0].uid);
    });
    return () => unsub();
  }, [selectedUid]);

  /* ---------------- Load all assets (for stats + search + per-employee counts) ---------------- */
  useEffect(() => {
    const unsub = onValue(rtdbRef(db, "/assetsByUser"), (snap) => {
      const all = snap.val() || {};
      setAllAssetsByUser(all);

      // counts by user
      const cbu = {};
      let totalUnits = 0;
      let assigned = 0;
      let laptopsMac = 0;
      let laptopsWin = 0;
      let mouse = 0;

      Object.entries(all).forEach(([uid, items]) => {
        const arr = Object.values(items || {});
        cbu[uid] = arr.length;
        totalUnits += arr.length;

        arr.forEach((row) => {
          const st = String(row?.status || "").toUpperCase();
          if (st === "ASSIGNED") assigned += 1;
          if (isLaptop(row)) {
            if (isMac(row)) laptopsMac += 1;
            else laptopsWin += 1;
          }
          const cat = (row?.category || "").toLowerCase();
          if (cat.includes("mouse")) mouse += 1;
        });
      });

      setCountsByUser(cbu);
      setStats({ totalUnits, assigned, laptopsMac, laptopsWin, mouse });
    });
    return () => unsub();
  }, []);

  /* ---------------- Load assets for selected employee (right pane) ---------------- */
  useEffect(() => {
    if (!selectedUid) {
      setAssets(null);
      return;
    }
    setLoadingAssets(true);
    const unsub = onValue(rtdbRef(db, `/assetsByUser/${selectedUid}`), (snap) => {
      const val = snap.val() || {};
      const arr = Object.entries(val).map(([id, v]) => ({ id, ...(v || {}) }));
      arr.sort((a, b) => {
        const ad = a.assignedAt || 0;
        const bd = b.assignedAt || 0;
        if (bd !== ad) return bd - ad;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
      setAssets(arr);
      setLoadingAssets(false);
    });
    return () => unsub();
  }, [selectedUid]);

  /* ---------------- Filters & helpers ---------------- */
  const filteredEmployees = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return employees;
    return employees.filter((e) =>
      (e.name || "").toLowerCase().includes(t) ||
      (e.email || "").toLowerCase().includes(t)
    );
  }, [q, employees]);

  const selectedEmp = employees.find((e) => e.uid === selectedUid) || null;

  const resetForm = (presetCategory = "Laptop") => {
    const presetName = presetCategory === "Other" ? "" : presetCategory.split(" / ")[0];
    setCatSearch("");
    setForm({
      category: presetCategory,
      name: presetName || "",
      serial: "",
      returnable: true,
      status: "ASSIGNED",
      accessories: "",
      assignedAt: new Date().toISOString().slice(0, 10),
      dueReturnAt: "",
      notes: "",
      photoFile: null,
      photoUrl: "",
    });
  };

  const startAdd = () => {
    setEditingId(null);
    resetForm("Laptop");
    setEditOpen(true);
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setCatSearch("");
    setForm({
      category: row.category || "Other",
      name: row.name || "",
      serial: row.serial || "",
      returnable: !!row.returnable,
      status: row.status || "ASSIGNED",
      accessories: Array.isArray(row.accessories) ? row.accessories.join(", ") : (row.accessories || ""),
      assignedAt: row.assignedAt ? new Date(row.assignedAt).toISOString().slice(0, 10) : "",
      dueReturnAt: row.dueReturnAt ? new Date(row.dueReturnAt).toISOString().slice(0, 10) : "",
      notes: row.notes || "",
      photoFile: null,
      photoUrl: row.photoUrl || "",
    });
    setEditOpen(true);
  };

  /* ---------------- File ops ---------------- */
  const handleUploadPhotoIfNeeded = async (uid, assetId, file, existingUrl) => {
    if (!file) return existingUrl || "";
    const ext = (file.name?.split(".").pop() || "jpg").toLowerCase();
    const sref = storageRef(storage, `assets/${uid}/${assetId}/photo.${ext}`);
    const snap = await uploadBytes(sref, file, { contentType: file.type || "image/jpeg" });
    const url = await getDownloadURL(snap.ref);
    return url;
  };

  const deletePhotoByUrl = async (url) => {
    if (!url) return;
    try {
      const sref = storageRef(storage, url); // v9 allows from download URL
      await deleteObject(sref);
    } catch {}
  };

  /* ---------------- Save / Delete / Status ---------------- */
  const handleSave = async () => {
    if (!selectedUid) return;
    const name = (form.name || "").trim();
    if (form.category === "Other" && !name) {
      alert("Please enter a name for 'Other' category.");
      return;
    }
    if (!name) form.name = form.category.split(" / ")[0];

    setSaving(true);
    try {
      const nodeRef = rtdbRef(db, `/assetsByUser/${selectedUid}`);
      const assetId = editingId || push(nodeRef).key;

      const photoUrl = await handleUploadPhotoIfNeeded(selectedUid, assetId, form.photoFile, form.photoUrl);

      const payload = {
        name: form.name,
        category: form.category,
        serial: (form.serial || "").trim(),
        returnable: !!form.returnable,
        status: form.status,
        accessories: (form.accessories || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        assignedAt: form.assignedAt ? new Date(form.assignedAt).getTime() : null,
        dueReturnAt: form.dueReturnAt ? new Date(form.dueReturnAt).getTime() : null,
        notes: form.notes || "",
        photoUrl: photoUrl || "",
        updatedAt: Date.now(),
      };

      if (!editingId) payload.createdAt = Date.now();

      await set(rtdbRef(db, `/assetsByUser/${selectedUid}/${assetId}`), {
        id: assetId,
        ...payload,
      });

      setSaving(false);
      setEditOpen(false);
    } catch (e) {
      setSaving(false);
      alert("Save failed: " + e.message);
    }
  };

  const confirmDelete = (row) => {
    setConfirm({
      open: true,
      title: "Delete asset?",
      body: `This will remove "${row.name || row.category}" from ${selectedEmp?.name}. The photo (if any) will also be deleted.`,
      onConfirm: async () => {
        try {
          if (row.photoUrl) await deletePhotoByUrl(row.photoUrl);
          await remove(rtdbRef(db, `/assetsByUser/${selectedUid}/${row.id}`));
          setConfirm({ open: false });
        } catch (e) {
          alert("Delete failed: " + e.message);
        }
      },
    });
  };

  const markReturned = (row) => {
    setConfirm({
      open: true,
      title: "Mark as returned?",
      body: `Mark "${row.name || row.category}" as RETURNED.`,
      onConfirm: async () => {
        try {
          await update(rtdbRef(db, `/assetsByUser/${selectedUid}/${row.id}`), {
            status: "RETURNED",
            returnedAt: Date.now(),
            updatedAt: Date.now(),
          });
          setConfirm({ open: false });
        } catch (e) {
          alert("Update failed: " + e.message);
        }
      },
    });
  };

  const restoreAssigned = (row) => {
    setConfirm({
      open: true,
      title: "Set back to ASSIGNED?",
      body: `Revert "${row.name || row.category}" status to ASSIGNED.`,
      onConfirm: async () => {
        try {
          await update(rtdbRef(db, `/assetsByUser/${selectedUid}/${row.id}`), {
            status: "ASSIGNED",
            updatedAt: Date.now(),
          });
          setConfirm({ open: false });
        } catch (e) {
          alert("Update failed: " + e.message);
        }
      },
    });
  };

  const copy = async (t) => {
    try { await navigator.clipboard.writeText(t || ""); } catch {}
  };
  // --- Reusable confirm dialog ---
    function Confirm({ open, title, body, onClose, onConfirm, confirmColor = "error" }) {
    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{title || "Are you sure?"}</DialogTitle>
        <DialogContent dividers>
            <Typography variant="body2" color="text.secondary">
            {body || "This action cannot be undone."}
            </Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => onClose(false)}>Cancel</Button>
            <Button variant="contained" color={confirmColor} onClick={onConfirm}>
            Confirm
            </Button>
        </DialogActions>
        </Dialog>
    );
    }


  /* ---------------- Global search across all users ---------------- */
  useEffect(() => {
    const q = globalQuery.trim().toLowerCase();
    if (!q) {
      setGlobalResults([]);
      return;
    }
    const results = [];
    const byUid = allAssetsByUser || {};
    const empById = Object.fromEntries(employees.map(e => [e.uid, e]));

    Object.entries(byUid).forEach(([uid, items]) => {
      Object.values(items || {}).forEach((row) => {
        const hay = `${row?.name || ""} ${row?.serial || ""} ${row?.category || ""}`.toLowerCase();
        if (hay.includes(q)) {
          results.push({
            uid,
            employeeName: empById[uid]?.name || uid,
            asset: row
          });
        }
      });
    });

    // show latest assigned first
    results.sort((a, b) => (b?.asset?.assignedAt || 0) - (a?.asset?.assignedAt || 0));
    setGlobalResults(results.slice(0, 50)); // cap UI
  }, [globalQuery, allAssetsByUser, employees]);

  const employeeUnits = countsByUser[selectedUid] || 0;

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      {/* Header */}
      <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>Assets Manager</Typography>
          <Typography variant="body2" color="text.secondary">
            Assign, track, and manage company assets for employees.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: { xs: 1, md: 0 } }} alignItems="center">
          {/* Global asset search */}
          <TextField
            size="small"
            placeholder="Search any asset by name or serial…"
            value={globalQuery}
            onChange={(e) => setGlobalQuery(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={startAdd}
            disabled={!selectedUid}
          >
            Add Asset
          </Button>
        </Stack>
      </Stack>

      {/* DASHBOARD: stats summary */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
        <StatCard icon={<InventoryIcon />} label="Total Units" value={stats.totalUnits} />
        <StatCard icon={<AssignmentTurnedInIcon />} label="Assigned" value={stats.assigned} />
        <StatCard icon={<LaptopMacIcon />} label="Laptops" value={stats.laptopsMac + stats.laptopsWin} sub={`Mac ${stats.laptopsMac} • Windows ${stats.laptopsWin}`} />
        <StatCard icon={<MouseIcon />} label="Mouse" value={stats.mouse} />
      </Stack>

      {/* Global Search Results */}
      {globalQuery && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Search results ({globalResults.length})
            </Typography>
            {globalResults.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No matching assets.</Typography>
            ) : (
              <List dense sx={{ maxHeight: 220, overflow: "auto" }}>
                {globalResults.map((r, i) => (
                  <ListItemButton
                    key={`${r.uid}-${r.asset.id || i}`}
                    onClick={() => setSelectedUid(r.uid)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 24, height: 24 }} variant="rounded">
                            {iconFor(r.asset.category)}
                          </Avatar>
                          <Typography sx={{ fontWeight: 600 }}>
                            {r.asset.name || r.asset.category || "Asset"}
                          </Typography>
                          {r.asset.serial && (
                            <Chip label={r.asset.serial} size="small" variant="outlined" />
                          )}
                        </Stack>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {r.employeeName} • {r.asset.category} • {String(r.asset.status || "ASSIGNED")}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        {/* Left: Employee list (scrollable, fixed pane) */}
        <Card
          sx={{
            flexBasis: { xs: "100%", md: 340 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            maxHeight: { xs: "50vh", md: "calc(100vh - 180px)" },
          }}
        >
          <CardContent sx={{ pb: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search employee..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </CardContent>
          <Divider />
          <Box sx={{ overflowY: "auto", px: 1, py: 0.5, flexGrow: 1 }}>
            <List dense disablePadding>
              {filteredEmployees.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  No employees found.
                </Typography>
              )}
              {filteredEmployees.map((e, i) => (
                <ListItemButton
                  key={e.uid}
                  selected={selectedUid === e.uid}
                  onClick={() => setSelectedUid(e.uid)}
                  sx={{ borderRadius: 1, mb: 0.25 }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Chip label={i + 1} size="small" sx={{ height: 20, minWidth: 28, fontSize: 11 }} />
                        <Typography sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                          {e.name}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${countsByUser[e.uid] || 0}`}
                          sx={{ ml: "auto" }}
                        />
                      </Stack>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {e.email || e.uid}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Card>

        {/* Right: Assets table */}
        <Card sx={{ flex: 1, minHeight: 360, display: "flex", flexDirection: "column" }}>
          <CardContent sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column" }}>
            <Box sx={{ p: 2, pb: 0 }}>
              {selectedEmp ? (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Avatar sx={{ bgcolor: "primary.light" }}>{(selectedEmp.name || "?").slice(0,1)}</Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {selectedEmp.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedEmp.email} • UID: <code>{selectedEmp.uid}</code>
                    </Typography>
                  </Box>
                  <Chip color="primary" label={`Units: ${employeeUnits}`} />
                </Stack>
              ) : (
                <Alert severity="info" sx={{ mb: 1 }}>Select an employee to manage assets.</Alert>
              )}
            </Box>

            {loadingAssets && <LinearProgress />}

            {/* Scrollable table region */}
            <Box sx={{ flex: 1, overflow: "auto", px: 2, pb: 2, width: "100%", maxWidth: "100%" }}>
              {assets && assets.length === 0 && (
                <Alert severity="info">No assets assigned yet. Click “Add Asset”.</Alert>
              )}

              {assets && assets.length > 0 && (
                <Table
                  size="small"
                  sx={{
                    minWidth: 850,
                    tableLayout: "auto",
                    "& td, & th": { whiteSpace: "nowrap" },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Asset</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Serial / MAC</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Returnable</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Assigned</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Due</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assets.map((a, idx) => (
                      <TableRow key={a.id} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar variant="rounded" sx={{ width: 28, height: 28 }} src={a.photoUrl || undefined}>
                              {a.photoUrl ? <ImageIcon fontSize="small" /> : iconFor(a.category)}
                            </Avatar>
                            <Box sx={{ maxWidth: 360 }}>
                              <Typography sx={{ fontWeight: 600, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {a.name || a.category || "Asset"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {a.category || "Other"}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              {a.serial || "—"}
                            </Typography>
                            {a.serial && (
                              <Tooltip title="Copy">
                                <IconButton size="small" onClick={() => copy(a.serial)}>
                                  <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{a.returnable ? "Yes" : "No"}</TableCell>
                        <TableCell>{fmtDate(a.assignedAt) || "—"}</TableCell>
                        <TableCell>{fmtDate(a.dueReturnAt) || "—"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={String(a.status || "ASSIGNED")}
                            color={statusChipColor(a.status)}
                            variant={a.status === "RETURNED" ? "outlined" : "filled"}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {a.photoUrl && (
                              <Tooltip title="Open photo">
                                <IconButton size="small" onClick={() => window.open(a.photoUrl, "_blank")}>
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => startEdit(a)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {a.status !== "RETURNED" ? (
                              <Tooltip title="Mark returned">
                                <IconButton size="small" onClick={() => markReturned(a)}>
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Set back to ASSIGNED">
                                <IconButton size="small" onClick={() => restoreAssigned(a)}>
                                  <UndoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => confirmDelete(a)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </CardContent>
        </Card>
      </Stack>

      {/* Add/Edit dialog — same compact look, with Category Search + Chips */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? "Edit Asset" : "Add Asset"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {/* Category search + chips */}
            <TextField
              size="small"
              placeholder="Search category (e.g., laptop, charger)…"
              value={catSearch}
              onChange={(e) => setCatSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {catOptions.map((c) => {
                const active = form.category === c;
                return (
                  <Chip
                    key={c}
                    icon={iconFor(c)}
                    label={c}
                    color={active ? "primary" : "default"}
                    variant={active ? "filled" : "outlined"}
                    onClick={() =>
                      setForm((p) => ({
                        ...p,
                        category: c,
                        name: c === "Other" ? (p.name || "") : c.split(" / ")[0],
                      }))
                    }
                    sx={{ mb: 1 }}
                  />
                );
              })}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              If nothing matches your search, “Other” is available — type a custom asset name below.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                size="small"
                label="Asset name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder={form.category === "Other" ? "e.g., Keyboard" : form.category}
                fullWidth
                required={form.category === "Other"}
              />
              <TextField
                size="small"
                label="Serial / MAC / ID"
                value={form.serial}
                onChange={(e) => setForm((p) => ({ ...p, serial: e.target.value }))}
                fullWidth
              />
            </Stack>

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!form.returnable}
                  onChange={(e) => setForm((p) => ({ ...p, returnable: e.target.checked }))}
                />
              }
              label="Returnable"
            />

            <TextField
              size="small"
              label="Accessories (comma separated)"
              value={form.accessories}
              onChange={(e) => setForm((p) => ({ ...p, accessories: e.target.value }))}
              placeholder="Charger, Bag"
              fullWidth
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                size="small"
                type="date"
                label="Assigned on"
                InputLabelProps={{ shrink: true }}
                value={form.assignedAt}
                onChange={(e) => setForm((p) => ({ ...p, assignedAt: e.target.value }))}
                fullWidth
              />
              <TextField
                size="small"
                type="date"
                label="Due (optional)"
                InputLabelProps={{ shrink: true }}
                value={form.dueReturnAt}
                onChange={(e) => setForm((p) => ({ ...p, dueReturnAt: e.target.value }))}
                fullWidth
              />
            </Stack>

            <TextField
              size="small"
              label="Notes"
              multiline
              minRows={2}
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              fullWidth
            />

            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">Photo (optional)</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                <Button variant="outlined" component="label" startIcon={<ImageIcon />}>
                  Choose photo
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => setForm((p) => ({ ...p, photoFile: e.target.files?.[0] || null }))}
                  />
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  {(form.photoFile && form.photoFile.name) || (form.photoUrl ? "Existing photo" : "No file chosen")}
                </Typography>
                {form.photoUrl && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar variant="rounded" src={form.photoUrl} sx={{ width: 56, height: 56 }} />
                    <Button size="small" onClick={() => window.open(form.photoUrl, "_blank")} endIcon={<OpenInNewIcon />}>
                      Open
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving || !selectedUid} onClick={handleSave}>
            {saving ? "Saving…" : editingId ? "Save changes" : "Add asset"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm */}
      <Confirm
        open={!!confirm.open}
        title={confirm.title || ""}
        body={confirm.body || ""}
        onClose={() => setConfirm({ open: false })}
        onConfirm={confirm.onConfirm || (() => setConfirm({ open: false }))}
        confirmColor={confirm.confirmColor || "error"}
      />
    </Box>
  );
}
